/**
 * Money in the currency the price is actually in.
 *
 * The account has sold prices in currencies other than USD, so a display that
 * hardcodes `$` misreports them. The code comes from the Stripe price object,
 * which is always ISO-4217.
 *
 * "Always" is the provider's promise, not ours: the value reaches us as a bare
 * string, and `Intl.NumberFormat` throws `RangeError` on anything that is not
 * three letters. A price formatter that throws takes the whole purchase surface
 * down over a display detail, so an unusable code degrades to the plain amount
 * with the code beside it.
 */
export function formatPrice(
  amount: number,
  currency: string,
  options?: Intl.NumberFormatOptions,
): string {
  const code = currency.toUpperCase();
  const numberOptions = {
    // Cents only when the price has them: $39 and $39.99 are both real amounts
    // on this account, and rounding either one misquotes the charge.
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  };

  if (/^[A-Za-z]{3}$/.test(currency)) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
        ...numberOptions,
      }).format(amount);
    } catch {
      // Well-formed but unknown to this runtime's ICU data — fall through.
    }
  }

  const formatted = new Intl.NumberFormat('en-US', {
    ...numberOptions,
    style: 'decimal',
    currency: undefined,
  }).format(amount);
  return code === '' ? formatted : `${formatted} ${code}`;
}
